import { useCallback, useMemo } from 'react';

import {
  filterIaSuggestions,
  IaPromptName,
  Session,
  useIsAdmin,
  useIsBeta,
  useRecruitment,
  useRequestSessionIaSuggestion,
  useSessionIaSuggestion,
} from '@app/api';

export const useIaSuggestion = <Result>(
  session: Session,
  promptName: IaPromptName,
) => {
  const isAdmin = useIsAdmin();
  const isBeta = useIsBeta();
  const { data: recruitment } = useRecruitment(session.recruitmentId);
  const available = (!!isAdmin || !!isBeta) && !!recruitment?.targetInformation;

  const { data: suggestions, isSuccess: ready } = useSessionIaSuggestion(
    available ? session.id : undefined,
  );
  const attempts = useMemo(
    () => filterIaSuggestions<Result>(suggestions, promptName),
    [suggestions, promptName],
  );

  const answered = useMemo(
    () => attempts.filter((attempt) => !!attempt.result),
    [attempts],
  );
  const pending = attempts.some((attempt) => attempt.generationInProgress);

  const { mutate, isLoading } = useRequestSessionIaSuggestion(session.id);
  const request = useCallback(() => mutate(promptName), [mutate, promptName]);

  const requestFirst = useCallback(() => {
    if (ready && attempts.length === 0 && !isLoading) {
      request();
    }
  }, [ready, attempts.length, isLoading, request]);

  return {
    available,
    ready,
    attempts: answered,
    request,
    requestFirst,
    generating: isLoading || pending,
  };
};
